import { Injectable, computed, signal, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { GetCurrentUserData } from '../../../../core/store/current-user/current-user.state';
import { EnrollmentNotificationService } from '../../../../core/services/enrollment-notification.service';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { CustomPortalError } from '../../../../core/errors/models/CustomPortalError';
import { finalize, map, Observable, switchMap } from 'rxjs';

import { LearningPlanService } from './learning-plan.service';
import { LearningPlanDialogService } from './learning-plan-dialog.service';
import { UserService } from '../../../organization/user/services/user.service';
import { UserCreationNotificationService } from '../../../organization/user/services/user-creation-notification.service';

import { LearningPlanInstanceDetailUiModel } from '../models/LearningPlanInstanceDetailUIModel';
import { LearningPlanEnrolledStudentUIModel } from '../models/LearningPlanEnrolledStudentUIModel';
import {
  LearningPlanInstanceDetailDTO,
  LearningPlanStatus,
} from '../models/LearningPlanDTO';
import {
  enrolledStudentCourseUserActivityDtoToLearningPlanEnrolledStudentUIModel,
  toLearningPlanEnrolledStudentUIModel,
  toLearningPlanInstanceDetailUiModel,
} from '../mappers/dto-mappers';
import { PrecuratedLearningPlanDetailsDto } from '../models/PrecuratedLearningPlanDTO';
import { TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from '../../../../core/services/analytics/analytics.service';
import { CertificateExportService } from '../../certificate/services/certificate-export.service';
import { CertificateLanguage } from '../../certificate/models/CertificateLanguage';
import { CertificateTrainingPeriod } from '../../certificate/models/CertificateExportRequest';
import {
  certificateBundleFileName,
  certificateFileName,
} from '../../certificate/certificate-file-name';

interface SelectOption {
  key: string;
  value: string;
  startDate: string;
  endDate: string;
}

@Injectable({ providedIn: 'root' })
export class LearningPlanDataService {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly certificateExportService = inject(CertificateExportService);
  private readonly learningPlanService = inject(LearningPlanService);
  private readonly snackbar = inject(SnackbarService);
  private readonly enrollmentNotification = inject(
    EnrollmentNotificationService,
  );
  private readonly dialogService = inject(LearningPlanDialogService);
  private readonly userService = inject(UserService);
  private readonly userCreationNotificationService = inject(
    UserCreationNotificationService,
  );
  private readonly store = inject(Store);

  private readonly arrowRedrawTriggerSignal = signal(0);

  readonly learningPlanInstanceId = signal<string | undefined>(undefined);
  readonly learningPlanName = signal<string | undefined>(undefined);

  readonly details = signal<LearningPlanInstanceDetailUiModel | null>(null);
  private readonly _precuratedLearningPlans = signal<
    PrecuratedLearningPlanDetailsDto[]
  >([]);
  readonly precuratedLearningPlans = this._precuratedLearningPlans.asReadonly();
  private readonly _isLoadingPrecuratedLearningPlans = signal<boolean>(false);
  readonly isLoadingPrecuratedLearningPlans =
    this._isLoadingPrecuratedLearningPlans.asReadonly();
  private readonly translocoService = inject(TranslocoService);
  readonly selectedTabIndex = signal<number>(0);

  readonly periodPickerOptions = computed<SelectOption[]>(() => {
    const d = this.details();
    if (!d?.periodPickerOptions) {
      return [];
    }

    const raw = d.periodPickerOptions.map((option) => ({
      startDate: option.startDate,
      endDate: option.endDate,
      key:
        this.formatDate(option.startDate) +
        ' - ' +
        this.formatDate(option.endDate),
    }));

    return raw.reverse().map((opt, reversedIndex) => ({
      ...opt,
      value: reversedIndex.toString(),
    }));
  });

  readonly periodPickerControl: FormControl<string | null> = new FormControl<
    string | null
  >('0');
  readonly periodPickerControlValue = signal<string | null>(
    this.periodPickerControl.value,
  );
  readonly selectedPeriodPickerControlOption = computed<SelectOption | null>(
    () => {
      const periodPickerControlValue = this.periodPickerControlValue();
      const periodPickerControlValueNumber = Number(periodPickerControlValue);
      if (!periodPickerControlValue || isNaN(periodPickerControlValueNumber)) {
        return null;
      }
      const selectOption = this.periodPickerOptions();

      if (
        periodPickerControlValueNumber < 0 ||
        periodPickerControlValueNumber >= selectOption.length
      ) {
        return null;
      }
      return this.periodPickerOptions()[periodPickerControlValueNumber];
    },
  );

  readonly courseIdByCourseInstanceId = computed<Map<string, string>>(() => {
    const phases = this.details()?.orderedPhases ?? [];
    return new Map(
      phases.flatMap((phase) =>
        phase.courseInstances.map(
          (courseInstance) =>
            [courseInstance.id, courseInstance.courseId] as const,
        ),
      ),
    );
  });

  readonly isCurrentPeriod = computed(() => {
    const opt = this.selectedPeriodPickerControlOption();
    if (!opt) {
      return false;
    }
    const now = new Date();
    return now >= new Date(opt.startDate) && now <= new Date(opt.endDate);
  });

  readonly canEdit = computed(
    () => this.details()?.status === LearningPlanStatus.SCHEDULED,
  );
  readonly canEnroll = computed(
    () => this.details()?.status !== LearningPlanStatus.ARCHIVED,
  );
  readonly hasDeadline = computed(
    () => this.details()?.trainingPhaseType === 'Custom',
  );
  readonly isDeadlinePassed = computed(() => {
    const deadline = this.details()?.orderedPhases[0]?.endsAt;
    return (
      this.hasDeadline() &&
      !!deadline &&
      new Date(deadline).getTime() < Date.now()
    );
  });
  readonly isLooping = computed(() => {
    const phases = this.details()?.orderedPhases;
    if (!phases?.length) {
      return false;
    }
    const [first, ...rest] = phases;
    const last = rest[rest.length - 1] ?? first;
    return last.nextPhaseId === first.id;
  });
  readonly isLoopingLoading = signal<boolean>(false);

  readonly tableStudents = signal<LearningPlanEnrolledStudentUIModel[]>([]);
  readonly isLoadingStudents = signal<boolean>(true);

  readonly arrowRedrawTrigger = this.arrowRedrawTriggerSignal.asReadonly();

  constructor() {
    this.enrollmentNotification.getEnrollmentChanged().subscribe((changed) => {
      if (changed && this.learningPlanInstanceId()) {
        this.getLearningPlanEnrollments();
      }
    });

    this.userCreationNotificationService
      .getUserChangedNotification()
      .subscribe((changed) => {
        if (changed && this.learningPlanInstanceId()) {
          this.getLearningPlanEnrollments();
        }
      });

    this.periodPickerControl.valueChanges.subscribe((value) => {
      this.isLoadingStudents.set(true);
      this.tableStudents.set([]);
      this.periodPickerControlValue.set(value);
      const learningPlanId = this.learningPlanInstanceId();
      const selectedPeriodPickerControlOption =
        this.selectedPeriodPickerControlOption();
      if (!learningPlanId || !selectedPeriodPickerControlOption) {
        return;
      }
      this.learningPlanService
        .getLearningPlanCourseUserActivities(
          learningPlanId,
          selectedPeriodPickerControlOption.startDate,
          selectedPeriodPickerControlOption.endDate,
        )
        .subscribe({
          next: (response) => {
            const phases = this.details()?.orderedPhases;
            const students =
              enrolledStudentCourseUserActivityDtoToLearningPlanEnrolledStudentUIModel(
                response.students,
                phases,
              );
            this.enrichStudentsWithAccountStatus(students).subscribe({
              next: (
                enrichedStudents: LearningPlanEnrolledStudentUIModel[],
              ) => {
                this.tableStudents.set(enrichedStudents);
                this.isLoadingStudents.set(false);
              },
              error: () => {
                this.tableStudents.set(students);
                this.isLoadingStudents.set(false);
              },
            });
          },
          error: () => {
            this.isLoadingStudents.set(false);
          },
        });
    });

    this.periodPickerControlValue.set(this.periodPickerControl.value);
  }

  triggerArrowRedraw() {
    this.arrowRedrawTriggerSignal.update((current) => current + 1);
  }

  reset(): void {
    this.learningPlanInstanceId.set(undefined);
    this.learningPlanName.set(undefined);
    this.details.set(null);
    this.periodPickerControl.setValue(null, { emitEvent: true });
    this.selectedTabIndex.set(0);
    this.isLoopingLoading.set(false);
  }

  getLearningPlanDetails(id: string) {
    this.learningPlanService.getLearningPlanInstanceDetails(id).subscribe({
      next: (res) => {
        this.setBasicLearningPlanData(res.learningPlanInstance);

        const options = this.periodPickerOptions();
        if (options.length) {
          const now = new Date();
          const currentIndex = options.findIndex(
            (opt) =>
              now >= new Date(opt.startDate) && now <= new Date(opt.endDate),
          );

          const defaultIndex = currentIndex >= 0 ? currentIndex : 0;
          this.periodPickerControl.setValue(defaultIndex.toString(), {
            emitEvent: true,
          });
        } else {
          this.getLearningPlanEnrollments();
        }
      },
      error: (err: CustomPortalError) => {
        this.snackbar.error(err.errorType);
      },
    });
  }

  getLearningPlanEnrollments() {
    const id = this.learningPlanInstanceId();
    if (!id) {
      return;
    }
    this.isLoadingStudents.set(true);
    this.learningPlanService.getLearningPlanEnrollments(id).subscribe({
      next: (response) => {
        const phases = this.details()?.orderedPhases;
        const students = toLearningPlanEnrolledStudentUIModel(
          response.students,
          phases,
        );
        this.enrichStudentsWithAccountStatus(students).subscribe({
          next: (enrichedStudents: LearningPlanEnrolledStudentUIModel[]) => {
            this.tableStudents.set(enrichedStudents);
            this.isLoadingStudents.set(false);
          },
          error: () => {
            this.tableStudents.set(students);
            this.isLoadingStudents.set(false);
          },
        });
      },
      error: (err: CustomPortalError) => {
        this.snackbar.error(err.errorType);
        this.isLoadingStudents.set(false);
      },
    });
  }

  unenrollStudents(students: LearningPlanEnrolledStudentUIModel[]) {
    const id = this.learningPlanInstanceId();
    if (!id) {
      return;
    }
    this.learningPlanService
      .unenrollStudentsFromLearningPlan(students, id)
      .subscribe({
        next: () => {
          this.enrollmentNotification.setEnrollmentChanged();
        },
        error: (err: CustomPortalError) => {
          this.snackbar.error(err.errorType);
        },
      });
  }

  sendReminder(students: LearningPlanEnrolledStudentUIModel[]) {
    this.learningPlanService.sendReminder(students).subscribe({
      error: (err: CustomPortalError) => {
        this.snackbar.error(err.errorType);
      },
    });
  }

  setLooping(looping: boolean) {
    const id = this.learningPlanInstanceId();
    if (!id) {
      return;
    }
    this.isLoopingLoading.set(true);

    this.learningPlanService
      .updateLearningPlanInstanceRepetition(id, { isLooping: looping })
      .pipe(
        switchMap(() =>
          this.learningPlanService.getLearningPlanInstanceDetails(id),
        ),
      )
      .subscribe({
        next: (res) => {
          this.setBasicLearningPlanData(res.learningPlanInstance);

          this.snackbar.success(
            'portal.trainings.detailPage.repetition.successfulUpdateSnackbar',
            {
              name: res.learningPlanInstance.name,
            },
          );
          if (looping) {
            this.analyticsService.track('recurrence_activated');
          } else {
            this.analyticsService.track('recurrence_deactivated');
          }
          this.store.dispatch(new GetCurrentUserData());
          this.isLoopingLoading.set(false);

          // Trigger arrow redraw after all updates are complete
          setTimeout(() => {
            this.triggerArrowRedraw();
          }, 0);
        },
        error: (err: CustomPortalError) => {
          this.snackbar.error(err.errorType);
          this.isLoopingLoading.set(false);
        },
      });
  }

  setBasicLearningPlanData(
    learningPlanInstance: LearningPlanInstanceDetailDTO,
  ) {
    this.learningPlanName.set(learningPlanInstance.name);
    const details = toLearningPlanInstanceDetailUiModel(learningPlanInstance);
    this.details.set(details);
  }

  exportStudentInfoToCsv() {
    const name = this.learningPlanName();
    const learningPlanId = this.learningPlanInstanceId();
    const period = this.getExportPeriod();

    if (!learningPlanId || !period) {
      return;
    }
    const { startDate, endDate } = period;

    this.learningPlanService
      .exportStudentInfoToCsv(learningPlanId, startDate, endDate)
      .subscribe({
        next: (data) => {
          const filename = `${name ?? 'learning-plan'}_[${startDate.slice(0, 10)}_${endDate.slice(0, 10)}]_Exported${new Date().toISOString().slice(0, 10)}`;
          this.openFile(data, filename);
        },
        error: (err: CustomPortalError) => {
          this.snackbar.error(err.errorType);
        },
      });
  }

  exportCertificate(
    student: LearningPlanEnrolledStudentUIModel,
    language: CertificateLanguage,
  ) {
    const period = this.getExportPeriod();
    const courseId = this.courseIdByCourseInstanceId().get(
      student.courseInstanceId,
    );
    if (!period || !courseId) {
      return;
    }
    this.certificateExportService.exportCertificate(
      {
        courseId,
        userId: student.id,
        language,
        period,
      },
      certificateFileName(
        language,
        [
          this.localizedCourseTitle(student.courseName, language),
          student.username,
        ],
        student.completedOn,
      ),
    );
  }

  exportCertificateBundle(
    students: LearningPlanEnrolledStudentUIModel[],
    language: CertificateLanguage,
  ) {
    const period = this.getExportPeriod();
    const learningPlanInstanceId = this.learningPlanInstanceId();
    if (!period || !learningPlanInstanceId || !students.length) {
      return;
    }
    this.certificateExportService.exportCertificateBundle(
      {
        learningPlanInstanceId,
        userIds: students.map((student) => student.id),
        language,
        period,
      },
      certificateBundleFileName(
        language,
        [this.learningPlanName() ?? 'learning-plan'],
        period,
      ),
    );
  }

  private localizedCourseTitle(
    courseName: string | undefined,
    language: CertificateLanguage,
  ): string {
    if (!courseName) {
      return '';
    }
    const key = `portal.courses.courseTitleKeys.${courseName}.name`;
    const title = this.translocoService.translate(key, {}, language);
    return title === key ? courseName : title;
  }

  private getExportPeriod(): CertificateTrainingPeriod | null {
    if (!this.hasDeadline()) {
      const option = this.selectedPeriodPickerControlOption();
      return option
        ? { startDate: option.startDate, endDate: option.endDate }
        : null;
    }

    const startDate = this.details()?.startAt;
    if (!startDate) {
      return null;
    }
    const deadline = this.details()?.orderedPhases[0]?.endsAt;
    const now = new Date();
    const endDate =
      deadline && new Date(deadline) > now ? deadline : now.toISOString();
    return { startDate, endDate };
  }

  deleteLearningPlan(onSuccess?: () => void) {
    const id = this.learningPlanInstanceId();

    const translationPath =
      'portal.trainings.trainingNameKeys.' + this.learningPlanName();
    const name =
      this.translocoService.translate(translationPath) !== translationPath
        ? this.translocoService.translate(translationPath)
        : this.learningPlanName();

    if (!id) {
      return;
    }
    const dialog = this.dialogService.showDeleteLearningPlanDialog(() => {
      if (dialog.buttonRowData) {
        dialog.buttonRowData.confirmButtonLoading = true;
      }
      this.learningPlanService.deleteLearningPlanInstance(id).subscribe({
        next: () => {
          this.store.dispatch(new GetCurrentUserData());
          this.snackbar.success(
            'portal.trainings.detailPage.successfulDeleteSnackbar',
            { name },
          );
          if (dialog.buttonRowData) {
            dialog.buttonRowData.confirmButtonLoading = false;
          }
          dialog.closeDialog();
          this.reset();
          if (onSuccess) {
            onSuccess();
          }
        },
        error: (err: CustomPortalError) => {
          this.snackbar.error(err.errorType);
          if (dialog.buttonRowData) {
            dialog.buttonRowData.confirmButtonLoading = false;
          }
        },
      });
    });
  }

  formatDate(isoString: string): string {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  }

  private enrichStudentsWithAccountStatus(
    students: LearningPlanEnrolledStudentUIModel[],
  ): Observable<LearningPlanEnrolledStudentUIModel[]> {
    return this.userService.getUsers().pipe(
      map((usersResponse) => {
        const userStatusMap = new Map(
          usersResponse.users.map((u) => [u.id, u.accountEnabled]),
        );

        return students.map((student) => ({
          ...student,
          participantStatus: userStatusMap.get(student.id)
            ? 'active'
            : 'inactive',
        }));
      }),
    );
  }

  private openFile(data: Blob, filename: string) {
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  loadPrecuratedLearningPlans(topicAreaId: string | undefined): void {
    if (!topicAreaId) {
      this._isLoadingPrecuratedLearningPlans.set(false);
      this._precuratedLearningPlans.set([]);
      return;
    }

    this._isLoadingPrecuratedLearningPlans.set(true);
    this._precuratedLearningPlans.set([]);

    this.learningPlanService
      .getPrecuratedLearningPlans(topicAreaId)
      .pipe(
        finalize(() => {
          this._isLoadingPrecuratedLearningPlans.set(false);
        }),
      )
      .subscribe({
        next: (plans) => {
          this._precuratedLearningPlans.set(plans);
        },
        error: (err: CustomPortalError) => {
          this.snackbar.error(err.errorType);
        },
      });
  }
}
